/**
 * Voice STT/TTS provider settings (ADR-0025) — lives on the Odino settings page.
 *
 * Two independent stages (listen / speak). Each picks a provider: an external
 * commercial API (OpenAI, ElevenLabs) that takes an API key (cifrata nel vault),
 * or "Browser (Web Speech)" — the keyless default that uses the client's own
 * speech edges. There is no self-hosted speech sidecar.
 *
 * Native `<select>` is intentional: this is the operator console (the portal
 * "no native select" rule doesn't apply here).
 */
import { useState } from "react";

import {
  type VoiceProviderInfo,
  type VoiceSettings,
  type VoiceStageSettings,
  odinoApi,
} from "@/lib/api-odino";

const inputCls = "w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm";

function StageEditor({
  title,
  hint,
  stage,
  save,
  showVoice,
}: {
  title: string;
  hint: string;
  stage: VoiceStageSettings;
  save: (body: { provider: string; model?: string; voice?: string; api_key?: string }) => Promise<void>;
  showVoice: boolean;
}) {
  const [provider, setProvider] = useState(stage.provider ?? stage.providers[0]?.provider_id ?? "");
  const [model, setModel] = useState(stage.model ?? "");
  const [voice, setVoice] = useState(stage.voice ?? "");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const current: VoiceProviderInfo | undefined = stage.providers.find((p) => p.provider_id === provider);
  const needsKey = current?.needs_key ?? false;

  const onSave = async () => {
    setSaving(true);
    setErr(null);
    setOk(false);
    try {
      await save({
        provider,
        ...(model ? { model } : {}),
        ...(showVoice && voice ? { voice } : {}),
        ...(apiKey ? { api_key: apiKey } : {}),
      });
      setApiKey("");
      setOk(true);
    } catch {
      setErr("Salvataggio fallito (chiave mancante alla prima configurazione?)");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-1 flex items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {stage.configured && (
          <span className="rounded-full bg-success/15 px-2 py-0.5 text-[10px] font-medium text-success">
            configurato
          </span>
        )}
      </div>
      <p className="mb-3 text-[11px] text-muted-foreground">{hint}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium uppercase tracking-wide text-muted-foreground">Provider</span>
          <select className={inputCls} value={provider} onChange={(e) => setProvider(e.target.value)}>
            {stage.providers.map((p) => (
              <option key={p.provider_id} value={p.provider_id}>
                {p.label}
              </option>
            ))}
          </select>
          {current?.description && (
            <span className="text-[11px] text-muted-foreground">{current.description}</span>
          )}
        </label>
        {showVoice && (
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium uppercase tracking-wide text-muted-foreground">Voce</span>
            <input
              className={inputCls}
              list={`voice-${title}`}
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              placeholder="es. nova / voice_id"
            />
            <datalist id={`voice-${title}`}>
              {(current?.voices ?? []).map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </label>
        )}
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium uppercase tracking-wide text-muted-foreground">Modello</span>
          <input
            className={inputCls}
            list={`model-${title}`}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={needsKey ? "es. gpt-4o-mini-tts" : "(non richiesto)"}
          />
          <datalist id={`model-${title}`}>
            {(current?.models ?? []).map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </label>
        {needsKey && (
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium uppercase tracking-wide text-muted-foreground">
              API key {stage.configured && "(vuoto = invariata)"}
            </span>
            <input
              className={inputCls}
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={stage.configured ? "••••••••" : "sk-…"}
            />
          </label>
        )}
      </div>
      {err && <p className="mt-3 text-xs text-danger">{err}</p>}
      {ok && <p className="mt-3 text-xs text-success">Impostazioni salvate.</p>}
      <button
        type="button"
        onClick={() => void onSave()}
        disabled={saving || !provider}
        className="mt-4 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground disabled:opacity-50"
      >
        {saving ? "Salvataggio…" : "Salva"}
      </button>
    </div>
  );
}

export function VoiceSettingsForm({
  settings,
  onSaved,
}: {
  settings: VoiceSettings;
  onSaved: (s: VoiceSettings) => void;
}) {
  return (
    <div className="grid gap-4">
      <StageEditor
        title="Ascolto (STT)"
        hint="Trascrizione vocale. API esterna (OpenAI) oppure browser (Web Speech)."
        stage={settings.stt}
        showVoice={false}
        save={async (b) => onSaved(await odinoApi.putVoiceStt(b))}
      />
      <StageEditor
        title="Voce (TTS)"
        hint="Voce di risposta. API esterna (OpenAI / ElevenLabs) oppure browser (Web Speech)."
        stage={settings.tts}
        showVoice
        save={async (b) => onSaved(await odinoApi.putVoiceTts(b))}
      />
    </div>
  );
}
